import { FormEvent, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { primaryLogoPngUrl } from "@/config/public-assets";
import { resetAdminPassword } from "@/features/auth/api/adminAuthApi";
import { authMessages } from "@/features/auth/messages";
import type { ForgotPasswordLocationState } from "@/features/auth/types/loginFlow";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import styles from "./LoginPage.module.css";

const MIN_PASSWORD_LENGTH = 12;

function PasswordVisibilityIcon({ visible }: { visible: boolean }) {
  if (visible) {
    return (
      <svg
        className={styles.passwordToggleIcon}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
      >
        <path
          d="M3 3l18 18M10.58 10.58A2 2 0 0 0 12 15a2 2 0 0 0 1.42-.58M9.88 5.09A10.94 10.94 0 0 1 12 5c5 0 9.27 3.11 11 7.5a11.8 11.8 0 0 1-1.67 2.83M6.11 6.11A11.75 11.75 0 0 0 1 12.5C2.73 16.89 7 20 12 20a10.9 10.9 0 0 0 5.11-1.27"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg
      className={styles.passwordToggleIcon}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d="M2 12C3.73 7.11 7.5 4 12 4s8.27 3.11 10 8c-1.73 4.89-5.5 8-10 8S3.73 16.89 2 12Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="3"
        stroke="currentColor"
        strokeWidth="1.75"
      />
    </svg>
  );
}

export default function ForgotPasswordPage() {
  useDocumentTitle(authMessages.documentTitle.forgotPassword);
  const { brand, forgotPassword: forgotMsg } = authMessages;
  const navigate = useNavigate();
  const location = useLocation();
  const flowState = location.state as ForgotPasswordLocationState | null;
  const email = flowState?.email?.trim() ?? "";

  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!email || !flowState?.message) {
    return <Navigate to="/login" replace />;
  }

  const notice = flowState.hint?.trim()
    ? `${flowState.message} ${flowState.hint}`
    : flowState.message;

  const canSubmit =
    token.trim().length > 0 &&
    newPassword.length >= MIN_PASSWORD_LENGTH &&
    confirmPassword.length > 0 &&
    !isSubmitting;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (!token.trim()) {
      setError(forgotMsg.tokenRequired);
      return;
    }

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(forgotMsg.passwordTooShort);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(forgotMsg.passwordMismatch);
      return;
    }

    setIsSubmitting(true);

    try {
      await resetAdminPassword({
        token: token.trim(),
        new_password: newPassword,
      });

      navigate("/login", {
        replace: true,
        state: { passwordResetSuccess: true },
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : forgotMsg.resetFailed,
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className={styles.page} data-testid="forgot-password-page">
      <main
        className={styles.card}
        aria-labelledby="forgot-password-heading"
        data-testid="forgot-password-card"
      >
        <div className={styles.brandH} data-testid="forgot-password-brand">
          <img
            src={primaryLogoPngUrl}
            alt={brand.name}
            className={styles.brandLogoFull}
            decoding="async"
            referrerPolicy="no-referrer"
          />
          <span className={styles.brandAdmin}>{brand.adminPortal}</span>
        </div>

        <p
          className={styles.formSuccess}
          role="status"
          data-testid="forgot-password-notice"
        >
          {notice}
        </p>

        {error ? (
          <p
            className={styles.formError}
            role="alert"
            data-testid="forgot-password-error"
          >
            {error}
          </p>
        ) : null}

        <h1
          id="forgot-password-heading"
          className={styles.heading}
          data-testid="forgot-password-title"
        >
          {forgotMsg.title}
        </h1>
        <p className={styles.subtext} data-testid="forgot-password-subtitle">
          {forgotMsg.subtitleBeforeEmail}{" "}
          <strong>{email}</strong>
          {forgotMsg.subtitleAfterEmail}
        </p>

        <form
          className={styles.form}
          onSubmit={handleSubmit}
          data-testid="forgot-password-form"
        >
          <div className={styles.field} data-testid="forgot-password-token-field">
            <label className={styles.label} htmlFor="forgot-password-token">
              {forgotMsg.token}
            </label>
            <input
              id="forgot-password-token"
              name="token"
              className={styles.input}
              type="text"
              autoComplete="off"
              spellCheck={false}
              placeholder={forgotMsg.tokenPlaceholder}
              value={token}
              onChange={(event) => {
                setToken(event.target.value);
                setError(null);
              }}
              disabled={isSubmitting}
              required
              aria-required="true"
              data-testid="forgot-password-token-input"
            />
          </div>

          <div
            className={styles.field}
            data-testid="forgot-password-new-password-field"
          >
            <label className={styles.label} htmlFor="forgot-password-new">
              {forgotMsg.newPassword}
            </label>
            <div className={styles.passwordInputWrap}>
              <input
                id="forgot-password-new"
                name="newPassword"
                className={styles.passwordInput}
                type={isNewPasswordVisible ? "text" : "password"}
                autoComplete="new-password"
                placeholder={authMessages.login.passwordPlaceholder}
                value={newPassword}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                  setError(null);
                }}
                disabled={isSubmitting}
                required
                minLength={MIN_PASSWORD_LENGTH}
                aria-required="true"
                aria-describedby="forgot-password-hint"
                data-testid="forgot-password-new-input"
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setIsNewPasswordVisible((visible) => !visible)}
                aria-label={
                  isNewPasswordVisible
                    ? authMessages.login.hidePassword
                    : authMessages.login.showPassword
                }
                aria-pressed={isNewPasswordVisible}
                disabled={isSubmitting}
                data-testid="forgot-password-new-visibility-toggle"
              >
                <PasswordVisibilityIcon visible={isNewPasswordVisible} />
              </button>
            </div>
            <p id="forgot-password-hint" className={styles.fieldHint}>
              {forgotMsg.passwordHint}
            </p>
          </div>

          <div
            className={styles.field}
            data-testid="forgot-password-confirm-field"
          >
            <label className={styles.label} htmlFor="forgot-password-confirm">
              {forgotMsg.confirmPassword}
            </label>
            <div className={styles.passwordInputWrap}>
              <input
                id="forgot-password-confirm"
                name="confirmPassword"
                className={styles.passwordInput}
                type={isConfirmPasswordVisible ? "text" : "password"}
                autoComplete="new-password"
                placeholder={authMessages.login.passwordPlaceholder}
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  setError(null);
                }}
                disabled={isSubmitting}
                required
                minLength={MIN_PASSWORD_LENGTH}
                aria-required="true"
                data-testid="forgot-password-confirm-input"
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() =>
                  setIsConfirmPasswordVisible((visible) => !visible)
                }
                aria-label={
                  isConfirmPasswordVisible
                    ? authMessages.login.hidePassword
                    : authMessages.login.showPassword
                }
                aria-pressed={isConfirmPasswordVisible}
                disabled={isSubmitting}
                data-testid="forgot-password-confirm-visibility-toggle"
              >
                <PasswordVisibilityIcon visible={isConfirmPasswordVisible} />
              </button>
            </div>
          </div>

          <button
            type="submit"
            className={styles.submit}
            disabled={!canSubmit}
            aria-busy={isSubmitting}
            data-testid="forgot-password-submit"
          >
            {isSubmitting ? forgotMsg.submitting : forgotMsg.submit}
          </button>
        </form>

        <div className={styles.footerLinks} data-testid="forgot-password-footer">
          <Link to="/login" className={styles.footerLink}>
            {forgotMsg.backToSignIn}
          </Link>
        </div>
      </main>
    </div>
  );
}
